/**
 * Market Stats
 *
 * A simplified Contentful component that displays market-level statistics.
 * Uses the marketIdentifier from CMS to fetch and display market metrics.
 */
import { Box, Text } from '@opendoor/bricks-next';
import { globalObservability } from '@opendoor/observability/slim';
import { LandingPageSectionContainer } from 'components/landing-pages-v2/shared/Container';
import { Entry } from 'contentful';
import type { ILpComponentMarketStatsFields } from 'declarations/contentful';
import type { MarketMetrics } from 'helpers/marketMetrics';
import { getMarketMetrics } from 'helpers/marketMetrics';

const Sentry = globalObservability.getSentryClient();

/**
 * Data loader function - called server-side during getServerSideProps
 * This fetches the market metrics data from Athena before rendering
 */
export const loader = async (
  entry: Entry<ILpComponentMarketStatsFields>,
): Promise<MarketMetrics | null> => {
  const marketIdentifier = entry.fields.marketIdentifier;

  if (!marketIdentifier) {
    return null;
  }

  try {
    const metrics = await getMarketMetrics(marketIdentifier);
    return metrics;
  } catch (error) {
    Sentry?.captureException?.(error, {
      captureContext: {
        extra: {
          marketIdentifier,
          entryId: entry.sys.id,
        },
      },
    });
    return null;
  }
};

/**
 * Individual stat card component
 */
const StatCard: React.FC<{
  value: string | number;
  change?: number | null;
  label: string;
  sublabel?: string;
  reverseTrendColor?: boolean;
  fullWidth?: boolean;
}> = ({ value, change, label, sublabel, reverseTrendColor = false, fullWidth = false }) => {
  const getTrendColor = (
    changeValue: number | null | undefined,
  ): '$contentSecondary' | '$borderStateErrorDefault' | '$backgroundAccentBrand' => {
    if (changeValue === null || changeValue === undefined) {
      return '$contentSecondary';
    }
    if (reverseTrendColor) {
      return changeValue > 0 ? '$borderStateErrorDefault' : '$backgroundAccentBrand';
    }
    return changeValue < 0 ? '$borderStateErrorDefault' : '$backgroundAccentBrand';
  };

  const formatChange = (changeValue: number | null | undefined): string => {
    if (changeValue === null || changeValue === undefined) return '';
    const arrow = changeValue < 0 ? '↘' : '↗';
    const percentage = Math.round(Math.abs(changeValue) * 100);
    return `${arrow} ${percentage}%`;
  };

  return (
    <Box
      position="relative"
      marginTop="$4x"
      padding="$10x"
      backgroundColor="$backgroundPrimary"
      borderRadius="$8x"
      width="100%"
      $largerThanSM={{
        width: fullWidth ? '100%' : 'calc(50% - 4px)',
      }}
    >
      {change !== undefined && change !== null && (
        <Text
          tag="p"
          typography="$bodySmall"
          color={getTrendColor(change)}
          position="absolute"
          top="$10x"
          right="$10x"
        >
          {formatChange(change)}
        </Text>
      )}
      <Text tag="h4" typography="$headerSmall" color="$contentPrimary">
        {typeof value === 'number' ? value.toLocaleString('en-US') : value}
      </Text>
      <Text tag="p" typography="$bodySmall" marginTop="$4x" color="$contentPrimary">
        {label}
        {sublabel && (
          <>
            <br />
            {sublabel}
          </>
        )}
      </Text>
    </Box>
  );
};

/**
 * Main render function - renders the market stats
 *
 * @param entry - The Contentful entry for this component
 * @param metrics - Pre-fetched market metrics data from the loader
 */
const RenderMarketStats = (
  entry: Entry<ILpComponentMarketStatsFields>,
  metrics: MarketMetrics | null | undefined,
) => {
  if (!metrics) {
    return null;
  }

  const fields = entry.fields;

  return (
    <Box backgroundColor="$backgroundSecondary" width="100%">
      <LandingPageSectionContainer>
        <Box paddingVertical="$20x">
          {/* Header Section */}
          <Box
            display="flex"
            flexDirection="column"
            marginBottom="$8x"
            $largerThanSM={{ flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              $largerThanSM={{ width: '60%' }}
            >
              {fields.title && (
                <Text tag="h2" typography="$subheaderMedium" color="$contentPrimary">
                  {fields.title}
                </Text>
              )}
              {fields.subtitle && (
                <Text tag="p" typography="$bodyMedium" color="$contentSecondary" marginTop="$6x">
                  {fields.subtitle}
                </Text>
              )}
            </Box>
          </Box>

          {/* Stats Grid */}
          <Box display="flex" flexDirection="column">
            {/* Row 1: New Listings + Homes on Market (50/50 width) */}
            <Box
              display="flex"
              flexDirection="column"
              gap="$2x"
              width="100%"
              $largerThanSM={{
                flexDirection: 'row',
                gap: '$2x',
                alignItems: 'stretch',
                flexWrap: 'nowrap',
              }}
            >
              {metrics.newListings !== undefined && metrics.newListings !== null && (
                <StatCard
                  value={metrics.newListings}
                  change={metrics.newListingsWow}
                  label="New listings"
                  sublabel="This week"
                />
              )}

              {metrics.homesOnMarket !== undefined && metrics.homesOnMarket !== null && (
                <StatCard
                  value={metrics.homesOnMarket}
                  change={metrics.homesOnMarketWow}
                  label="Homes on market"
                  sublabel="Currently active"
                />
              )}
            </Box>

            {/* Row 2: Homes Delisted + Homes Sold (50/50 width) */}
            <Box
              display="flex"
              flexDirection="column"
              gap="$2x"
              width="100%"
              $largerThanSM={{
                flexDirection: 'row',
                gap: '$2x',
                alignItems: 'stretch',
                flexWrap: 'nowrap',
              }}
            >
              {metrics.delistings !== undefined && metrics.delistings !== null && (
                <StatCard
                  value={metrics.delistings}
                  change={metrics.delistingsWow}
                  label="Homes delisted"
                  sublabel="This week"
                />
              )}

              {metrics.homesSold !== undefined && metrics.homesSold !== null && (
                <StatCard
                  value={metrics.homesSold}
                  change={metrics.homesSoldWow}
                  label="Homes sold"
                  sublabel="This week"
                />
              )}
            </Box>
          </Box>

          {/* Timestamp */}
          {metrics.timePeriod && (
            <Text
              marginTop="$12x"
              tag="p"
              typography="$labelSmall"
              color="$contentSecondary"
              textAlign="center"
            >
              Last updated on{' '}
              {new Date(metrics.timePeriod).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                timeZone: 'UTC',
              })}
            </Text>
          )}
        </Box>
      </LandingPageSectionContainer>
    </Box>
  );
};

const MarketStats = {
  render: RenderMarketStats,
  loader: loader,
};

export default MarketStats;
